import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAdminSkill,
  fetchAdminAgeGroups,
  fetchAdminCategories,
  fetchAdminSkills,
  updateAdminSkill,
  updateAdminSkillStatus,
} from "@/admin/api";
import type { AdminAgeGroup, AdminCategory, AdminSkill } from "@/admin/types";
import LanguageSelect from "@/components/feature/LanguageSelect";
import ExportButton from "@/components/feature/ExportButton";
import MediaPicker, { getTablerIconByName } from "@/components/feature/MediaPicker";
import Pagination from "@/components/feature/Pagination";
import Modal from "@/components/ui/Modal";
import IconRenderer from "@/components/feature/IconRenderer";
import AdminListCard from "@/pages/admin/components/AdminListCard";

type FormState = {
  ageGroupId: string;
  categoryId: string;
  iconUrl: string;
  videoUrl: string;
  translations: Record<string, string>;
};

type SortKey = "skillName" | "categoryName" | "ageGroupName" | "status";
type SortDirection = "asc" | "desc";
type StatusFilter = "all" | "active" | "inactive";

const isImageUrl = (value: string) => {
  const lower = value.toLowerCase();
  return lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".gif") || lower.endsWith(".webp") || lower.endsWith(".svg");
};

const renderIconPreview = (value: string) => {
  return <IconRenderer iconUrl={value} size={18} />;
};



export default function AdminSkills() {
  const { t, i18n } = useTranslation("common");
  const [skills, setSkills] = useState<AdminSkill[]>([]);
  const [ageGroups, setAgeGroups] = useState<AdminAgeGroup[]>([]);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterAgeGroupId, setFilterAgeGroupId] = useState("");
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("skillName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);

  const languages = useMemo(() => Object.keys(i18n.options.resources || {}), [i18n.options.resources]);
  const currentLanguage = useMemo(() => (i18n.language || "ar").split("-")[0], [i18n.language]);
  const defaultLanguage = useMemo(() => {
    const fallback = i18n.options.fallbackLng;
    if (typeof fallback === "string") return fallback;
    if (Array.isArray(fallback)) return fallback[0] || "ar";
    if (fallback && typeof fallback === "object") {
      const entry = Object.values(fallback)[0];
      if (Array.isArray(entry)) return entry[0] || "ar";
      if (typeof entry === "string") return entry;
    }
    return "ar";
  }, [i18n.options.fallbackLng]);

  const buildTranslations = (skill?: AdminSkill) => {
    return languages.reduce<Record<string, string>>((acc, lng) => {
      const existing = skill?.translations?.find((item) => item.locale === lng);
      acc[lng] = existing?.title || "";
      return acc;
    }, {});
  };

  const getSkillTitle = (skill: AdminSkill) => {
    const direct = skill?.translations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = skill?.translations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return skill?.translations?.[0]?.title || skill.key || "";
  };

  const getAgeGroupTitle = (ageGroupId?: number) => {
    if (!ageGroupId) return "";
    const ageGroup = ageGroups.find((item) => item.id === ageGroupId);
    if (!ageGroup) return "";
    const direct = ageGroup?.translations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = ageGroup?.translations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return ageGroup?.translations?.[0]?.title || ageGroup.key || "";
  };

  const getCategoryName = (categoryId?: number) => {
    if (!categoryId) return "";
    const category = categories.find((item) => item.id === categoryId);
    if (!category) return "";
    const direct = category?.translations?.find((item) => item.locale === currentLanguage)?.name;
    if (direct) return direct;
    const fallback = category?.translations?.find((item) => item.locale === defaultLanguage)?.name;
    if (fallback) return fallback;
    return category?.translations?.[0]?.name || category.key || "";
  };

  const exportColumns = useMemo(() => ([
    { header: t("adminSkillId"), value: (skill: AdminSkill) => skill.id },
    { header: t("adminSkillName"), value: (skill: AdminSkill) => getSkillTitle(skill) },
    { header: t("adminCategoryName"), value: (skill: AdminSkill) => getCategoryName(skill.category?.id) },
    { header: t("adminAgeGroupName"), value: (skill: AdminSkill) => getAgeGroupTitle(skill.ageGroup?.id) },
    { header: t("adminStatus"), value: (skill: AdminSkill) => (skill.isActive ? t("adminActive") : t("adminInactive")) },
    { header: t("adminSkillIcon"), value: (skill: AdminSkill) => skill.iconUrl || "" },
    { header: t("adminSkillVideoUrl"), value: (skill: AdminSkill) => skill.videoUrl || "" },
  ]), [t, currentLanguage, defaultLanguage, ageGroups, categories]);

  const [form, setForm] = useState<FormState>({
    ageGroupId: "",
    categoryId: "",
    iconUrl: "",
    videoUrl: "",
    translations: buildTranslations(),
  });
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

  const loadSkills = () => {
    return fetchAdminSkills()
      .then((data) => setSkills(data))
      .catch(() => setSkills([]));
  };

  useEffect(() => {
    let isMounted = true;
    Promise.all([loadSkills(), fetchAdminAgeGroups(), fetchAdminCategories()])
      .then(([_, ageGroupsData, categoriesData]) => {
        if (!isMounted) return;
        setAgeGroups(ageGroupsData);
        setCategories(categoriesData);
      })
      .catch(() => {
        if (!isMounted) return;
        setAgeGroups([]);
        setCategories([]);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      translations: buildTranslations(editingId ? skills.find((s) => s.id === editingId) : undefined),
    }));
  }, [languages, editingId, skills]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategoryId, filterAgeGroupId, filterStatus, pageSize]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ ageGroupId: "", categoryId: "", iconUrl: "", videoUrl: "", translations: buildTranslations() });
    setSelectedLanguage(defaultLanguage);
    setError("");
  };

  const handleEdit = (skill: AdminSkill) => {
    setEditingId(skill.id);
    setForm({
      ageGroupId: String(skill.ageGroup?.id || ""),
      categoryId: String(skill.category?.id || ""),
      iconUrl: skill.iconUrl || "",
      videoUrl: skill.videoUrl || "",
      translations: buildTranslations(skill),
    });
    setSelectedLanguage(defaultLanguage);
    setError("");
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    setForm((prev) => {
      const current = prev.translations[language];
      if (current && current.trim().length > 0) {
        return prev;
      }
      const fallbackValue = prev.translations[defaultLanguage];
      if (!fallbackValue || fallbackValue.trim().length === 0) {
        return prev;
      }
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [language]: fallbackValue,
        },
      };
    });
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.ageGroupId || !form.categoryId) {
      setError(t("adminFillRequired"));
      return;
    }

    const translations = languages
      .map((lng) => ({ locale: lng, title: form.translations[lng]?.trim() || "" }))
      .filter((item) => item.title.length > 0);

    if (translations.length === 0) {
      setError(t("adminFillTranslations"));
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ageGroupId: Number(form.ageGroupId),
        categoryId: Number(form.categoryId),
        iconUrl: form.iconUrl.trim() || null,
        videoUrl: form.videoUrl.trim() || null,
        translations,
      };

      if (editingId) {
        await updateAdminSkill(editingId, payload);
      } else {
        await createAdminSkill(payload);
      }
      await loadSkills();
      resetForm();
      setIsModalOpen(false);
    } catch {
      setError(t("adminSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (skill: AdminSkill) => {
    await updateAdminSkillStatus(skill.id, !skill.isActive);
    await loadSkills();
  };

  const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
  const filteredSkills = useMemo(() => {
    return skills.filter((skill) => {
      if (filterCategoryId && skill.category?.id !== Number(filterCategoryId)) {
        return false;
      }
      if (filterAgeGroupId && skill.ageGroup?.id !== Number(filterAgeGroupId)) {
        return false;
      }
      if (filterStatus === "active" && !skill.isActive) {
        return false;
      }
      if (filterStatus === "inactive" && skill.isActive) {
        return false;
      }

      if (!normalizedSearch) return true;
      const skillTitle = getSkillTitle(skill).toLocaleLowerCase();
      const categoryName = getCategoryName(skill.category?.id).toLocaleLowerCase();
      const ageGroupTitle = getAgeGroupTitle(skill.ageGroup?.id).toLocaleLowerCase();
      return skillTitle.includes(normalizedSearch)
        || categoryName.includes(normalizedSearch)
        || ageGroupTitle.includes(normalizedSearch);
    });
  }, [skills, normalizedSearch, filterCategoryId, filterAgeGroupId, filterStatus, currentLanguage, defaultLanguage, ageGroups, categories]);

  const sortedSkills = useMemo(() => {
    const data = [...filteredSkills];
    const direction = sortDirection === "asc" ? 1 : -1;

    data.sort((left, right) => {
      if (sortKey === "status") {
        return (Number(left.isActive) - Number(right.isActive)) * direction;
      }

      const getText = (value: string) => value || "";
      let leftValue = "";
      let rightValue = "";

      if (sortKey === "skillName") {
        leftValue = getText(getSkillTitle(left));
        rightValue = getText(getSkillTitle(right));
      } else if (sortKey === "categoryName") {
        leftValue = getText(getCategoryName(left.category?.id));
        rightValue = getText(getCategoryName(right.category?.id));
      } else if (sortKey === "ageGroupName") {
        leftValue = getText(getAgeGroupTitle(left.ageGroup?.id));
        rightValue = getText(getAgeGroupTitle(right.ageGroup?.id));
      }

      return leftValue.localeCompare(rightValue, undefined, { sensitivity: "base" }) * direction;
    });

    return data;
  }, [filteredSkills, sortKey, sortDirection, currentLanguage, defaultLanguage, ageGroups, categories]);

  const totalResults = sortedSkills.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedSkills = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedSkills.slice(start, start + pageSize);
  }, [sortedSkills, safePage, pageSize]);



  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  const getSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return "↕";
    return sortDirection === "asc" ? "▲" : "▼";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t("adminSkills")}</h2>
          <p className="text-sm text-gray-500 mt-1">{t("adminSkillManageHint")}</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-l from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-md transition-all flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t("adminAddSkill")}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-2">{t("adminSkillSearch")}</label>
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t("adminSkillSearchPlaceholder")}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-2">{t("adminFilterCategory")}</label>
            <select
              value={filterCategoryId}
              onChange={(event) => setFilterCategoryId(event.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="">{t("adminAll")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {getCategoryName(category.id) || category.key}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-2">{t("adminFilterAgeGroup")}</label>
            <select
              value={filterAgeGroupId}
              onChange={(event) => setFilterAgeGroupId(event.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="">{t("adminAll")}</option>
              {ageGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {getAgeGroupTitle(group.id) || group.key}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-2">{t("adminFilterStatus")}</label>
            <select
              value={filterStatus}
              onChange={(event) => setFilterStatus(event.target.value as StatusFilter)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="all">{t("adminAll")}</option>
              <option value="active">{t("adminActive")}</option>
              <option value="inactive">{t("adminInactive")}</option>
            </select>
          </div>
          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-gray-500">{t("adminResultsCount", { count: totalResults })}</span>
            <ExportButton
              label={t("adminExportSkills")}
              loadingLabel={t("adminExporting")}
              filename={`skills-${new Date().toISOString().slice(0, 10)}`}
              rows={sortedSkills}
              columns={exportColumns}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 text-xs"
            />
          </div>
        </div>
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:block bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800/80 overflow-hidden shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <div className="min-w-[850px] divide-y divide-gray-50 dark:divide-zinc-800/50">
            <div className="grid grid-cols-6 gap-2 px-6 py-4 text-xs font-semibold text-gray-500 bg-gray-50/50 dark:bg-zinc-800/30">
              <button
                type="button"
                onClick={() => handleSort("skillName")}
                className="flex items-center gap-2 font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-zinc-300 transition-colors"
              >
                <span>{t("adminSkillName")}</span>
                <span className="text-[10px]">{getSortIndicator("skillName")}</span>
              </button>
              <span>{t("adminIconPreview")}</span>
              <button
                type="button"
                onClick={() => handleSort("ageGroupName")}
                className="flex items-center gap-2 font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-zinc-300 transition-colors"
              >
                <span>{t("adminAgeGroupName")}</span>
                <span className="text-[10px]">{getSortIndicator("ageGroupName")}</span>
              </button>
              <button
                type="button"
                onClick={() => handleSort("categoryName")}
                className="flex items-center gap-2 font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-zinc-300 transition-colors"
              >
                <span>{t("adminCategoryName")}</span>
                <span className="text-[10px]">{getSortIndicator("categoryName")}</span>
              </button>
              <button
                type="button"
                onClick={() => handleSort("status")}
                className="flex items-center gap-2 font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-zinc-300 transition-colors"
              >
                <span>{t("adminStatus")}</span>
                <span className="text-[10px]">{getSortIndicator("status")}</span>
              </button>
              <span className="text-left">{t("adminActions")}</span>
            </div>
            {paginatedSkills.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-50 dark:bg-zinc-800/60 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-gray-500 dark:text-zinc-400">{t("adminNoResults") || "لا توجد نتائج"}</p>
              </div>
            ) : (
              paginatedSkills.map((skill) => (
                <div key={skill.id} className="grid grid-cols-6 gap-2 px-6 py-4 text-sm items-center hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                  <span className="text-gray-800 dark:text-zinc-200 font-medium">{getSkillTitle(skill)}</span>
                  <span className="text-gray-700 flex items-center">
                    <div className="p-2 bg-gray-50 dark:bg-zinc-800/50 rounded-lg border border-gray-100 dark:border-zinc-800 shadow-sm">{renderIconPreview(skill.iconUrl)}</div>
                  </span>
                  <span className="text-gray-650 dark:text-zinc-350 font-medium bg-gray-50 dark:bg-zinc-800/50 border border-gray-100 dark:border-zinc-800 rounded-md px-2 py-1 w-max">{getAgeGroupTitle(skill.ageGroup?.id) || "-"}</span>
                  <span className="flex items-center gap-2 text-gray-700 dark:text-zinc-350">
                    <span className="w-3 h-3 rounded-full shrink-0 border border-gray-200" style={{ backgroundColor: skill.category?.colorHex || "#ccc" }} />
                    {getCategoryName(skill.category?.id) || "-"}
                  </span>
                  <div>
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                      skill.isActive ? "bg-emerald-100 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400" : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400"
                    }`}>
                      {skill.isActive ? t("adminActive") : t("adminInactive")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-start" dir="ltr">
                    <button
                      onClick={() => handleEdit(skill)}
                      className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                      title={t("adminEdit")}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => toggleStatus(skill)}
                      className={`p-2 rounded-lg transition-colors ${
                        skill.isActive ? "text-amber-500 hover:bg-amber-50 dark:hover:bg-zinc-800" : "text-emerald-500 hover:bg-emerald-50 dark:hover:bg-zinc-800"
                      }`}
                      title={skill.isActive ? t("adminDisable") : t("adminEnable")}
                    >
                      {skill.isActive ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Mobile Card List View */}
      <div className="block md:hidden space-y-4">
        {paginatedSkills.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800/85 p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-50 dark:bg-zinc-800/60 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-zinc-400">{t("adminNoResults") || "لا توجد نتائج"}</p>
          </div>
        ) : (
          paginatedSkills.map((skill) => (
            <AdminListCard
              key={skill.id}
              title={getSkillTitle(skill)}
              iconUrl={skill.iconUrl}
              iconFallback="ri-star-smile-line"
              isActive={skill.isActive}
              onEdit={() => handleEdit(skill)}
              onToggleStatus={() => toggleStatus(skill)}
              badges={[
                {
                  label: getAgeGroupTitle(skill.ageGroup?.id) || "-",
                  icon: "ri-time-line"
                },
                {
                  label: getCategoryName(skill.category?.id) || "-",
                  color: skill.category?.colorHex || "#ccc"
                }
              ]}
            />
          ))
        )}
      </div>

      <div className="border-t border-gray-100">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[30, 50, 100]}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? t("adminEditSkill") : t("adminAddSkill")}
        maxWidth="3xl"
        footer={
          <>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
            >
              {t("adminCancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {editingId ? t("adminUpdate") : t("adminCreate")}
            </button>
          </>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSkillAgeGroup")}</label>
              <select
                value={form.ageGroupId}
                onChange={(event) => setForm((prev) => ({ ...prev, ageGroupId: event.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white"
              >
                <option value="">{t("adminSelectAgeGroup")}</option>
                {ageGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {getAgeGroupTitle(group.id) || group.key}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSkillCategory")}</label>
              <select
                value={form.categoryId}
                onChange={(event) => setForm((prev) => ({ ...prev, categoryId: event.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white"
              >
                <option value="">{t("adminSelectCategory")}</option>
                {categories.filter((category) => category.isActive).map((category) => (
                  <option key={category.id} value={category.id}>
                    {getCategoryName(category.id) || category.key}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-1 md:col-span-2">
              <MediaPicker
                label={t("adminSkillIcon")}
                value={form.iconUrl}
                onChange={(nextValue) => setForm((prev) => ({ ...prev, iconUrl: nextValue }))}
              />
            </div>
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminSkillVideoUrl")}</label>
              <input
                value={form.videoUrl}
                onChange={(event) => setForm((prev) => ({ ...prev, videoUrl: event.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                placeholder="https://youtube.com/..."
              />
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="block text-xs font-semibold text-gray-600 mb-3">{t("adminTranslationValue")}</label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="w-full sm:w-32 flex-shrink-0">
                <LanguageSelect languages={languages} value={selectedLanguage} onChange={handleLanguageChange} />
              </div>
              <input
                value={form.translations[selectedLanguage] || ""}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    translations: { ...prev.translations, [selectedLanguage]: event.target.value },
                  }))
                }
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                placeholder={t("adminTranslationHint")}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}